export interface WebSocketMessage {
  type: string;
  content: any;
  id?: string;
}

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: number;
}

// Structured thinking step from the router
export interface ThinkingStep {
  id: string;           // Unique identifier for the step
  text: string;         // Description of the step
  type: string;         // Type: "analysis", "action", "execution", "result", "general"
  status: string;       // Status: "pending", "in_progress", "complete"
  target?: string;      // Target cluster or cloud connection name
  is_cloud?: boolean;   // Whether this is a cloud-related step
}

// Chat response content from WebSocket message
export interface ChatResponseContent {
  type: 'thinking' | 'answer' | 'acknowledgment' | 'cancelled' | 'text_complete';
  textChunk?: string;           // Raw text
  steps?: ThinkingStep[];       // Structured steps (for thinking type)
  done?: boolean;
  /** Replace the entire accumulated answer with textChunk instead of
   *  appending — used by the router to clean up a malformed stream (e.g.
   *  workflow JSON that arrived without its fence). */
  replace?: boolean;
}

// Risk Assessment message interfaces
export interface RiskAssessmentAgentUpdate {
  assessment_id: string;
  agent_type: string; // "orchestrator", "network_policy", "privilege_escalation", "rbac"
  thinking_overview: string;
  turn_number: number;
  status: string; // "investigating", "completed", "failed"
  findings?: string;
}

export interface RiskAssessmentMessage {
  assessment_id: string;
  status: string; // "started", "agent_thinking", "orchestrator_thinking", "completed", "failed"
  message?: string;
}

export interface RiskAssessmentFinalReport {
  assessment_id: string;
  final_report: string;
  status: string; // "completed", "failed"
  error_message?: string;
}

export interface YAMLFix {
  file_path: string;
  file_type: 'create' | 'update' | 'delete';
  yaml_content: string;
  existing_yaml?: string;
  explanation?: string;
  resource_name?: string;
  resource_kind?: string;
  resource_namespace?: string;
}

export interface YAMLFixesMessage {
  yaml_fixes: Record<string, YAMLFix[]>;
}

// Cloud fix interface for Terraform, CloudFormation, Pulumi, AWS CLI, OCI CLI
export interface CloudFix {
  fix_type: 'terraform' | 'cloudformation' | 'pulumi' | 'aws_cli' | 'oci_cli';
  resource_type: string;
  resource_name: string;
  action: 'create' | 'update' | 'delete';
  content: string;
  existing_content?: string;
  explanation?: string;
  file_path?: string;
}

export interface CloudFixesMessage {
  cloud_fixes: Record<string, CloudFix[]>;
}

// Code fix interface for source code fixes from connected GitHub/GitLab repos
export interface CodeFix {
  file_path: string;
  line_range: [number, number];
  original_code: string;
  fixed_code: string;
  explanation?: string;
  language?: string;
  repo_id?: string;
}

export interface CodeFixesMessage {
  code_fixes: Record<string, CodeFix[]>;
  code_provider?: string; // "github" or "gitlab"
}

export interface FindingChatMessage {
  finding_id: string;
  assessment_id: string;
  user_query: string;
  finding_details: any;
  affected_resources: any[];
}

export interface WebSocketClient {
  connect(): Promise<void>;
  disconnect(): void;
  sendMessage(message: WebSocketMessage): void;
  onMessage(callback: (message: WebSocketMessage) => void): void;
  offMessage(callback: (message: WebSocketMessage) => void): void; // Add method to remove handlers
  onConnect(callback: () => void): void;
  onDisconnect(callback: () => void): void;
  onError(callback: (error: Event) => void): void;
  isConnected(): boolean;
}

class WebSocketClientImpl implements WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectInterval: number = 3000;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private messageCallbacks: ((message: WebSocketMessage) => void)[] = [];
  private connectCallbacks: (() => void)[] = [];
  private disconnectCallbacks: (() => void)[] = [];
  private errorCallbacks: ((error: Event) => void)[] = [];
  private shouldReconnect: boolean = true;
  private isConnectionReady: boolean = false;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Determine WebSocket URL based on environment
    let wsUrl: string;

    // First check for environment variable override (for special cases)
    if (process.env.REACT_APP_WS_URL) {
      wsUrl = process.env.REACT_APP_WS_URL;
    } else {
      // Intelligent hostname-based WebSocket URL detection
      const hostname = window.location.hostname;

      // Check if we're on a Kestrel AI platform domain
      if (hostname.endsWith('platform.usekestrel.ai')) {
        // Extract the prefix (if any) from the hostname
        // Examples:
        // - platform.usekestrel.ai -> ws.platform.usekestrel.ai
        // - staging-platform.usekestrel.ai -> staging-ws.platform.usekestrel.ai
        // - dev-platform.usekestrel.ai -> dev-ws.platform.usekestrel.ai

        if (hostname === 'platform.usekestrel.ai') {
          // Production: no prefix
          wsUrl = 'wss://ws.platform.usekestrel.ai/api/ws';
        } else {
          // Try to extract prefix safely
          // Match pattern: <prefix>-platform.usekestrel.ai
          const match = hostname.match(/^(.+)-platform\.usekestrel\.ai$/);

          if (match && match[1]) {
            // Valid prefix found
            const prefix = match[1];
            wsUrl = `wss://${prefix}-ws.platform.usekestrel.ai/api/ws`;
          } else {
            // Could not extract a valid prefix, fall back to production
            console.warn('Could not extract prefix from hostname, using production WebSocket:', hostname);
            wsUrl = 'wss://ws.platform.usekestrel.ai/api/ws';
          }
        }
      } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Local development - connect to router
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host.replace(':3001', ':8080');
        wsUrl = `${protocol}//${host}/api/ws`;
      } else {
        // Fallback for other environments (e.g., custom domains, IP addresses)
        // Assume WebSocket is available at the same host
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${window.location.host}/api/ws`;
      }
    }

    // Log the WebSocket URL for debugging (can be removed in production)
    console.log(`WebSocket connecting to: ${wsUrl}`);

    this.url = wsUrl;
  }

  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        if (!sessionToken) {
          throw new Error('No session token found');
        }

        // Create WebSocket connection with session token in URL query parameter
        // Headers are not supported in browser WebSocket API, so we use query params
        const wsUrl = `${this.url}?token=${encodeURIComponent(sessionToken)}`;
        
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.reconnectAttempts = 0;
          // Small delay to ensure WebSocket is fully ready
          setTimeout(() => {
            this.isConnectionReady = true;
            this.startHeartbeat(); // Start client-side heartbeat
            this.connectCallbacks.forEach(callback => callback());
            resolve();
          }, 100);
        };

        this.ws.onmessage = (event) => {
          try {
            
            const messages = event.data.split('\n');
            messages.forEach((messageStr: string) => {
              if (messageStr.trim()) {
                const message: WebSocketMessage = JSON.parse(messageStr);
                
                // Handle ping messages from server
                if (message.type === 'ping') {
                  // Respond with pong
                  if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify({ type: 'pong' }));
                  }
                  return;
                }
                
                // Call all registered callbacks with a copy of the array to avoid issues if callbacks modify the array
                [...this.messageCallbacks].forEach(callback => callback(message));
              }
            });
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = () => {
          this.ws = null;
          this.isConnectionReady = false;
          this.stopHeartbeat(); // Stop client-side heartbeat
          this.disconnectCallbacks.forEach(callback => callback());
          
          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            // Exponential backoff with minimal jitter to prevent thundering herd
            const baseDelay = Math.min(30000, this.reconnectInterval * 2 ** this.reconnectAttempts);
            const delay = baseDelay + Math.random() * 1000;
            setTimeout(() => this.connect(), delay);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.errorCallbacks.forEach(callback => callback(error));
          // Manually close to ensure onclose handler runs and triggers reconnection logic
          try { this.ws?.close(); } catch {}
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.isConnectionReady = false;
    this.stopHeartbeat(); // Stop client-side heartbeat
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  sendMessage(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isConnectionReady) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected. Cannot send message:', message);
    }
  }

  onMessage(callback: (message: WebSocketMessage) => void): void {
    // Prevent duplicate registrations of the same callback
    if (!this.messageCallbacks.includes(callback)) {
      this.messageCallbacks.push(callback);
    }
  }

  offMessage(callback: (message: WebSocketMessage) => void): void {
    const index = this.messageCallbacks.indexOf(callback);
    if (index > -1) {
      this.messageCallbacks.splice(index, 1);
    }
  }

  onConnect(callback: () => void): void {
    this.connectCallbacks.push(callback);
  }

  onDisconnect(callback: () => void): void {
    this.disconnectCallbacks.push(callback);
  }

  onError(callback: (error: Event) => void): void {
    this.errorCallbacks.push(callback);
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN && this.isConnectionReady;
  }

  private startHeartbeat(): void {
    if (this.heartbeatInterval) return; // Prevent multiple intervals
    // Send ping every 15 seconds to keep GCP Load Balancer connection alive
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected()) {
        this.sendMessage({ type: 'ping', content: {} });
      }
    }, 15000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

// Export a singleton instance
export const webSocketClient = new WebSocketClientImpl();

// Helper function to send chat messages
export function sendChatMessage(message: string): string {
  const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  webSocketClient.sendMessage({
    type: 'chat',
    content: {
      message: message,
    },
    id: messageId,
  });
  
  return messageId;
}

// Helper function to send finding chat messages
export function sendFindingChatMessage(
  message: string,
  findingId: string,
  assessmentId: string,
  findingDetails: any,
  affectedResources: any[]
): string {
  const messageId = `finding-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  webSocketClient.sendMessage({
    type: 'finding_chat',
    content: {
      message: message,
      finding_id: findingId,
      assessment_id: assessmentId,
      finding_details: findingDetails,
      affected_resources: affectedResources,
    },
    id: messageId,
  });
  
  return messageId;
}

// Helper function to send cloud finding chat messages (supports both AWS and OCI)
export function sendCloudFindingChatMessage(
  message: string,
  findingId: string,
  findingDetails: any,
  cloudContext: {
    cloud_provider: 'aws' | 'oci';
    aws_account_id?: string;
    aws_connection_id?: string;
    oci_tenancy_ocid?: string;
    oci_connection_id?: string;
  }
): string {
  const messageId = `cloud-finding-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  webSocketClient.sendMessage({
    type: 'cloud_finding_chat',
    content: {
      message: message,
      finding_id: findingId,
      finding_details: findingDetails,
      cloud_provider: cloudContext.cloud_provider,
      aws_account_id: cloudContext.aws_account_id || '',
      aws_connection_id: cloudContext.aws_connection_id || '',
      oci_tenancy_ocid: cloudContext.oci_tenancy_ocid || '',
      oci_connection_id: cloudContext.oci_connection_id || '',
    },
    id: messageId,
  });

  return messageId;
}

// Helper function to send K8s incident chat messages
export function sendIncidentChatMessage(
  message: string,
  incidentId: string,
  incidentDetails: any,
  clusterName: string,
): string {
  const messageId = `incident-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  webSocketClient.sendMessage({
    type: 'incident_chat',
    content: {
      message: message,
      incident_id: incidentId,
      incident_details: incidentDetails,
      cluster_name: clusterName,
    },
    id: messageId,
  });

  return messageId;
}

// Helper function to send cloud incident chat messages (supports both AWS and OCI)
export function sendCloudIncidentChatMessage(
  message: string,
  incidentId: string,
  incidentDetails: any,
  cloudContext: {
    cloud_provider: 'aws' | 'oci';
    aws_account_id?: string;
    aws_connection_id?: string;
    oci_tenancy_ocid?: string;
    oci_connection_id?: string;
  }
): string {
  const messageId = `cloud-incident-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  webSocketClient.sendMessage({
    type: 'cloud_incident_chat',
    content: {
      message: message,
      incident_id: incidentId,
      incident_details: incidentDetails,
      cloud_provider: cloudContext.cloud_provider,
      aws_account_id: cloudContext.aws_account_id || '',
      aws_connection_id: cloudContext.aws_connection_id || '',
      oci_tenancy_ocid: cloudContext.oci_tenancy_ocid || '',
      oci_connection_id: cloudContext.oci_connection_id || '',
    },
    id: messageId,
  });

  return messageId;
}

export function sendWorkflowSuggestionsChatMessage(
  message: string,
  runbookIds?: string[],
  integrationSource?: { type: string; repo?: string; path?: string; space_key?: string; page_id?: string },
  builderContext?: {
    /** Serialized current builder canvas so the agent modifies instead of regenerating. */
    currentWorkflow?: any;
    /** Simulated builder mode: generation treats every integration as connected. */
    simulated?: boolean;
  },
): string {
  const messageId = `wf-suggest-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const content: Record<string, any> = { message };
  if (runbookIds && runbookIds.length > 0) content.runbook_ids = runbookIds;
  if (integrationSource) content.integration_source = integrationSource;
  if (builderContext?.currentWorkflow) content.current_workflow = builderContext.currentWorkflow;
  if (builderContext?.simulated) content.simulated = true;

  webSocketClient.sendMessage({
    type: 'workflow_suggestions_chat',
    content,
    id: messageId,
  });

  return messageId;
}

export function sendExecutionDiagnosisChatMessage(
  message: string,
  executionId: string,
  workflowId: string,
): string {
  const messageId = `exec-diag-msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  webSocketClient.sendMessage({
    type: 'execution_diagnosis',
    content: { message, execution_id: executionId, workflow_id: workflowId },
    id: messageId,
  });

  return messageId;
}

export function sendWorkflowRequestMessage(message: string, requestId?: string): string {
  const messageId = `wf-req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  webSocketClient.sendMessage({
    type: 'workflow_request',
    content: { message, request_id: requestId || undefined },
    id: messageId,
  });

  return messageId;
}

// Helper function to cancel ongoing chat requests
export function cancelChatMessage(messageId: string): string {
  const cancelId = `cancel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  webSocketClient.sendMessage({
    type: 'cancel',
    content: {
      message_id: messageId,
    },
    id: cancelId,
  });
  
  return cancelId;
}

// Helper function to start a new chat session
export function startNewChat(): string {
  const newChatId = `new-chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  webSocketClient.sendMessage({
    type: 'new_chat',
    content: {},
    id: newChatId,
  });
  
  return newChatId;
}

// Helper function to handle streaming responses with thinking support, YAML fixes, and Cloud fixes
export function handleStreamingResponse(
  messageId: string,
  onChunk: (chunk: string, replace?: boolean) => void,
  onComplete: () => void,
  onThinking?: (chunk: string, steps?: ThinkingStep[]) => void,
  onThinkingComplete?: () => void,
  onCancelled?: () => void,
  onYAMLFixes?: (fixes: Record<string, YAMLFix[]>) => void,
  onError?: (error: string) => void,
  onCloudFixes?: (fixes: Record<string, CloudFix[]>) => void,
  onTextComplete?: () => void,
  onDatadogMetrics?: (metrics: any[]) => void,
  onCodeFixes?: (fixes: Record<string, CodeFix[]>, codeProvider?: string) => void
): (message: WebSocketMessage) => void {
  return (message: WebSocketMessage) => {
    if (message.id === messageId) {
      if (message.type === 'chat_response' || message.type === 'finding_chat_response') {
        const content = message.content as ChatResponseContent;

        if (content.type === 'acknowledgment') {
          // Handle acknowledgment if needed
        } else if (content.type === 'thinking') {
          // Handle thinking chunks - pass both raw text and structured steps
          if (onThinking) {
            onThinking(content.textChunk || '', content.steps);
          }
        } else if (content.type === 'answer') {
          // Signal thinking is complete and start answer
          if (onThinkingComplete) {
            onThinkingComplete();
          }
          // Handle answer chunks (replace=true swaps the whole accumulated
          // answer for the corrected text instead of appending)
          onChunk(content.textChunk || '', content.replace === true);

          if (content.done) {
            onComplete();
          }
        } else if (content.type === 'text_complete') {
          // Text streaming is done, configuration fixes are loading
          if (onTextComplete) {
            onTextComplete();
          }
        } else if (content.type === 'cancelled') {
          // Handle cancellation acknowledgment
          if (onCancelled) {
            onCancelled();
          }
        }
      } else if (message.type === 'yaml_fixes' || message.type === 'finding_yaml_fixes') {
        // Handle YAML fixes (both regular and finding-specific)
        if (onYAMLFixes && message.content) {
          if (message.content.yaml_fixes) {
            onYAMLFixes(message.content.yaml_fixes);
          } else if (message.content.finding_yaml_fixes) {
            // Convert single cluster format to multi-cluster format for compatibility
            const clusterName = message.content.cluster_name || 'cluster';
            onYAMLFixes({ [clusterName]: message.content.finding_yaml_fixes });
          }
        }
      } else if (message.type === 'cloud_fixes') {
        // Handle Cloud fixes (Terraform, CloudFormation, AWS CLI)
        if (onCloudFixes && message.content) {
          if (message.content.cloud_fixes) {
            onCloudFixes(message.content.cloud_fixes);
          } else if (message.content.connection_name && message.content.fixes) {
            // Convert single connection format to multi-connection format
            onCloudFixes({ [message.content.connection_name]: message.content.fixes });
          }
        }
      } else if (message.type === 'datadog_metrics') {
        if (onDatadogMetrics && message.content?.metrics) {
          onDatadogMetrics(message.content.metrics);
        }
      } else if (message.type === 'code_fixes') {
        // Handle Code fixes (GitHub/GitLab source code fixes)
        if (onCodeFixes && message.content) {
          if (message.content.code_fixes) {
            onCodeFixes(message.content.code_fixes, message.content.code_provider);
          }
        }
      } else if (message.type === 'error') {
        // Handle error messages from the server
        if (onError) {
          const errorMessage = typeof message.content === 'string'
            ? message.content
            : message.content?.message || 'An error occurred';
          onError(errorMessage);
        }
      }
    }
  };
}

// Helper function to handle risk assessment agent thinking updates
export function handleRiskAssessmentAgentThinking(
  onAgentThinking: (update: RiskAssessmentAgentUpdate) => void
): (message: WebSocketMessage) => void {
  return (message: WebSocketMessage) => {
    if (message.type === 'risk_assessment_agent_thinking') {
      const update = message.content as RiskAssessmentAgentUpdate;
      onAgentThinking(update);
    }
  };
}

// Helper function to handle risk assessment status updates
export function handleRiskAssessmentUpdate(
  onStatusUpdate: (update: RiskAssessmentMessage) => void
): (message: WebSocketMessage) => void {
  return (message: WebSocketMessage) => {
    if (message.type === 'risk_assessment_update') {
      const update = message.content as RiskAssessmentMessage;
      onStatusUpdate(update);
    }
  };
}

// Helper function to handle risk assessment final reports
export function handleRiskAssessmentFinalReport(
  onFinalReport: (report: RiskAssessmentFinalReport) => void
): (message: WebSocketMessage) => void {
  return (message: WebSocketMessage) => {
    if (message.type === 'risk_assessment_final_report') {
      const report = message.content as RiskAssessmentFinalReport;
      onFinalReport(report);
    }
  };
}